import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { HMIField, HMILiveValues } from '../types';
import IndicatorField from './fields/IndicatorField';
import ControlField from './fields/ControlField';
import ActionField from './fields/ActionField';
import ChartField from './fields/ChartField';
import ImageOverlayField from './fields/ImageOverlayField';

export interface FieldRowProps {
  field: HMIField;
  value: unknown;
  /** Live value of `field.compareFieldId`, for dual-series chart/widget fields. */
  compareValue?: unknown;
  /** Every field in the template - only needed by `kind: "image"` fields, to resolve pinned fields' captions/units. */
  allFields: HMIField[];
  /** Full live-values map - only needed by `kind: "image"` fields, since their pins can reference fields in any section. */
  liveValues: HMILiveValues;
  disabled: boolean;
  onCommit: (value: unknown) => Promise<{ ok: boolean; error?: string }>;
  onRunAction: () => Promise<{ ok: boolean; error?: string }>;
}

/** One label + control row, shared layout across every screen. */
function FieldRow({ field, value, compareValue, allFields, liveValues, disabled, onCommit, onRunAction }: FieldRowProps) {
  const [error, setError] = useState<string | undefined>();

  const handleCommit = async (next: unknown) => {
    const result = await onCommit(next);
    setError(result.ok ? undefined : result.error);
  };

  // Charts/widgets need room to render a trend, so they get their own full-width block
  // instead of the compact label-left/control-right row every other field kind uses.
  if (field.kind === 'chart') {
    return (
      <Box sx={{ py: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }} title={field.notes}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {field.caption}
        </Typography>
        <ChartField field={field} value={value} compareValue={compareValue} />
      </Box>
    );
  }

  // Image-with-overlay widgets get the same full-width treatment as charts.
  if (field.kind === 'image') {
    return (
      <Box sx={{ py: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }} title={field.notes}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {field.caption}
        </Typography>
        <ImageOverlayField field={field} allFields={allFields} values={liveValues} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 0.75,
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        opacity: disabled ? 0.5 : 1,
      }}
      title={field.notes}
    >
      <Typography variant="body2" sx={{ minWidth: 220 }}>
        {field.caption}
      </Typography>
      <Box display="flex" flexDirection="column" alignItems="flex-end">
        {field.kind === 'indicator' && <IndicatorField field={field} value={value} />}
        {field.kind === 'control' && (
          <ControlField
            field={field}
            value={value}
            disabled={disabled}
            onCommit={handleCommit}
          />
        )}
        {field.kind === 'action' && (
          <ActionField
            field={field}
            value={value}
            disabled={disabled}
            onRun={onRunAction}
          />
        )}
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default FieldRow;
